import { push } from 'redux-first-history';
import { all, call, delay, put, race, select, takeEvery, takeLeading } from 'redux-saga/effects';

import * as EDITING_DOCUMENTS_STATUS from '@app/constants/editingDocumentsStatus';
import GLOBAL_ERROR_MESSAGE from '@app/constants/errorMessages';
import { getCurrentScaleSize } from '@app/libs/fieldViewport';
import { scaleSelector } from '@app/libs/reactPdfJs/store/selectors';
import workspaceService from '@app/libs/workspaceService';
import {
  EDITING_DOCUMENT__COMPLETE_EDIT,
  EDITING_DOCUMENT__FIELD_DATA_SET,
  EDITING_DOCUMENT__FIELD_TARGET_DATA_SET,
  EDITING_DOCUMENT__FILE_LOAD_REQUEST,
  EDITING_DOCUMENT__SUBMIT_REQUEST,
} from '@app/store/actionTypes';
import { recordGAEvent, recordGTMEvent, withAnalytics } from '@app/store/analytics/actions';
import {
  editingDocumentFieldCreate,
  editingDocumentFieldDataSet,
  editingDocumentFieldTargetDataSet,
  editingDocumentFieldTargetSet,
  editingDocumentLoadFile,
  editingDocumentLoadFileFailure,
  editingDocumentLoadFileSuccess,
  editingDocumentSubmitFailure,
  editingDocumentSubmitRequest,
  editingDocumentSubmitSuccess,
} from '@app/store/editingDocument/actions';
import EditingDocumentInput from '@app/store/editingDocument/EditingDocumentInput';
import EditingDocumentInputFieldType from '@app/store/editingDocument/EditingDocumentInputFieldType';
import EditingDocumentSignatureFieldType from '@app/store/editingDocument/EditingDocumentSignatureFieldType';
import {
  getEditingDocumentId,
  getFieldIdsInPages,
  getFieldInformation,
  getFieldPositions,
  getFieldSizes,
  getFieldTarget,
  getLatestFieldData,
  getLatestTextField,
  isFieldInserting,
} from '@app/store/editingDocument/selectors';
import FieldType from '@app/store/FieldType';
import FontType from '@app/store/FontType';
import { modalOpen } from '@app/store/modal/actions';
import TextAlign from '@app/store/TextAlign';
import {
  getEditingDocumentsFile,
  putEditingDocumentsFile,
} from '@app/store/transport-layer/document/editing-documents/file';
import { getEditingDocumentsStatus } from '@app/store/transport-layer/document/editing-documents/status';

/**
 * 특정 페이지의 x, y에 input을 추가한다.
 */
export function* setFieldData({
  payload: { page, relativeX, relativeY },
}: ReturnType<typeof editingDocumentFieldDataSet>) {
  const target: ReturnType<typeof getFieldTarget> = yield select(getFieldTarget);
  const scale: ReturnType<typeof scaleSelector> = yield select(scaleSelector);
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { fieldType, height, id, type, width, uuid, ...rest } = target!;

  const sizes: ReturnType<typeof getFieldSizes> = yield select(getFieldSizes);
  const latestFieldIDByType: ReturnType<typeof getLatestFieldData> =
    yield select(getLatestFieldData);
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const size = sizes[latestFieldIDByType[fieldType]!];

  let [currentScaleHeight, currentScaleWidth] = getCurrentScaleSize(fieldType, scale);

  if (size) {
    currentScaleHeight = size.currentScaleHeight;
    currentScaleWidth = size.currentScaleWidth;
  }

  yield put(
    editingDocumentFieldCreate({
      meta: {
        currentScaleHeight,
        currentScaleWidth,
        id: id as string,
        height: height as number,
        page,
        relativeX,
        relativeY,
        width: width as number,
        uuid,
      },
      // @ts-expect-error FIXME
      information: {
        ...rest,
        fieldType: fieldType as
          | FieldType.CHECKBOX_FIELD
          | FieldType.SIGNATURE_FIELD
          | FieldType.TEXT_FIELD,
        type: type as
          | FieldType.CHECKBOX_FIELD
          | FieldType.TEXT_FIELD
          | EditingDocumentSignatureFieldType,
        id: id as string,
        uuid,
      },
    })
  );
}

/**
 * 입력할 서명을 inputs 타겟으로 설정한다.
 */
export function* setFieldTargetData({
  payload: data,
}: ReturnType<typeof editingDocumentFieldTargetDataSet>) {
  const scale: ReturnType<typeof scaleSelector> = yield select(scaleSelector);
  let height = 100;
  let width = 260;
  const fieldType = data.fieldType || FieldType.SIGNATURE_FIELD;
  let information = {};

  if (data.type && data.type.indexOf('도장') !== -1) {
    height = 122;
    width = 122;
  }

  if (data.fieldType === FieldType.CHECKBOX_FIELD) {
    height = 15;
    width = 15;
    information = {
      checked: true,
      type: FieldType.CHECKBOX_FIELD,
    };
  }

  if (data.fieldType === FieldType.TEXT_FIELD) {
    // @ts-expect-error FIXME
    const latestTextField = yield select(getLatestTextField);
    if (latestTextField) {
      const { fontFamily, fontSize, textAlign } = latestTextField;
      information = {
        fontFamily,
        fontSize,
        textAlign,
        type: FieldType.TEXT_FIELD,
        value: '',
      };
    } else {
      information = {
        fontFamily: FontType.NOTO_SANS,
        fontSize: 12,
        textAlign: TextAlign.LEFT,
        type: FieldType.TEXT_FIELD,
        value: '',
      };
    }

    height = 17;
    width = 160;
  }

  yield put(
    editingDocumentFieldTargetSet({
      ...data,
      ...information,
      fieldType,
      height: height / scale,
      uuid: data.uuid,
      width: width / scale,
    })
  );
}

export function* loadFileSaga({ payload: id }: ReturnType<typeof editingDocumentLoadFile>) {
  try {
    const file: string = yield call(getEditingDocumentsFile, workspaceService.getWorkspaceId(), id);
    yield put(editingDocumentLoadFileSuccess(file));
  } catch (error) {
    yield put(editingDocumentLoadFileFailure(error));
    yield put(
      modalOpen('CONFIRM', {
        title: '문서 불러오기 실패',
        content: GLOBAL_ERROR_MESSAGE[error.message] || error.message,
      })
    );
  }
}

export function* pollEditingDocumentStatus(editingDocumentId: string) {
  const statuses = [EDITING_DOCUMENTS_STATUS.AVAILABLE, EDITING_DOCUMENTS_STATUS.FAILED];

  while (true) {
    const { status }: { status: string } = yield call(
      getEditingDocumentsStatus,
      workspaceService.getWorkspaceId(),
      editingDocumentId
    );

    if (statuses.includes(status)) {
      return status;
    }
    yield delay(1000);
  }
}

export function* submitEditingDocument({
  payload: { id, inputs },
}: ReturnType<typeof editingDocumentSubmitRequest>) {
  const timeoutMessage = '내용을 저장하는 중입니다.\n완료까지 약 1~2분 정도 걸릴 수 있습니다.';
  try {
    yield call(putEditingDocumentsFile, workspaceService.getWorkspaceId(), id, { inputs });
    const [status, timeout]: (string | boolean)[] = yield race([
      call(pollEditingDocumentStatus, id),
      delay(30000),
    ]);

    if (status) {
      if (status === EDITING_DOCUMENTS_STATUS.AVAILABLE) {
        const textFieldCount = inputs.filter(
          (v) => v.fieldType === EditingDocumentInputFieldType.TEXT
        ).length;
        yield put(
          withAnalytics(
            [
              recordGAEvent('문서편집하기', '텍스트입력', id, textFieldCount),
              recordGAEvent('문서편집하기', '문서편집완료', id),
              recordGTMEvent('editingDocCreated'),
            ],
            editingDocumentSubmitSuccess()
          )
        );
        yield delay(250);
        yield put(
          modalOpen('EDITOR_SINGLE_COMPLETE_MODAL', {
            title: '편집 완료',
            content: '편집된 문서로 템플릿을 만들거나\n서명 요청시 이용할 수 있습니다.',
          })
        );
      }
      if (status === EDITING_DOCUMENTS_STATUS.FAILED) {
        throw new Error('알 수 없는 오류로 인해\n문서 편집에 실패하였습니다.');
      }
    } else if (timeout) {
      yield put(push('/editing-documents'));
      throw new Error(timeoutMessage);
    }
  } catch (error) {
    const isTimeout = error && error.message === timeoutMessage;
    yield put(editingDocumentSubmitFailure({ isTimeout }));
    yield put(
      modalOpen('CONFIRM', {
        title: isTimeout ? '문서 처리 중' : '문서 편집 실패',
        content: GLOBAL_ERROR_MESSAGE[error.message] || error.message,
      })
    );
  }
}

export function* completeEditEditingDocument() {
  const isInserting: ReturnType<typeof isFieldInserting> = yield select(isFieldInserting);

  if (isInserting) {
    return;
  }

  const editingDocumentId: ReturnType<typeof getEditingDocumentId> =
    yield select(getEditingDocumentId);
  const ids: ReturnType<typeof getFieldIdsInPages> = yield select(getFieldIdsInPages);
  const information: ReturnType<typeof getFieldInformation> = yield select(getFieldInformation);
  const positions: ReturnType<typeof getFieldPositions> = yield select(getFieldPositions);
  const sizes: ReturnType<typeof getFieldSizes> = yield select(getFieldSizes);

  let validateTextField = false;
  const inputs: EditingDocumentInput[] = [];

  Object.keys(ids).forEach((page) => {
    ids[page].forEach((id) => {
      const informationById = information[id];
      const positionsById = positions[id];
      const sizesById = sizes[id];
      if (informationById && informationById.fieldType) {
        let object: EditingDocumentInput;
        if (informationById.fieldType === FieldType.SIGNATURE_FIELD) {
          object = {
            fieldType: EditingDocumentInputFieldType.SIGNATURE,
            locationX: positionsById.relativeX,
            locationY: positionsById.relativeY,
            locationZ: 0,
            locationPage: positionsById.page,
            sizeWidth: sizesById.relativeWidth,
            sizeHeight: sizesById.relativeHeight,
            signatureValueImageKey: informationById.s3Key,
            signatureValueSignatureId: informationById.id.toString(), // Must be string
          };
          inputs.push(object);
        } else if (informationById.fieldType === FieldType.TEXT_FIELD) {
          if (!informationById.value) {
            validateTextField = true;
            return;
          }

          object = {
            fieldType: EditingDocumentInputFieldType.TEXT,
            locationX: positionsById.relativeX,
            locationY: positionsById.relativeY,
            locationZ: 0,
            locationPage: positionsById.page,
            sizeWidth: sizesById.relativeWidth,
            sizeHeight: sizesById.relativeHeight,
            textStyleAlign: informationById.textAlign,
            textStyleFontType: informationById.fontFamily,
            textStyleFontSize: informationById.fontSize,
            textValueText: informationById.value,
          };
          inputs.push(object);
        } else {
          object = {
            fieldType: EditingDocumentInputFieldType.CHECKBOX,
            locationX: positionsById.relativeX,
            locationY: positionsById.relativeY,
            locationZ: 0,
            locationPage: positionsById.page,
            sizeWidth: sizesById.relativeWidth,
            sizeHeight: sizesById.relativeHeight,
          };
          inputs.push(object);
        }
      }
    });
  });

  if (validateTextField) {
    yield put(
      modalOpen('SINGLE_SIGNATURE_TEXT_FIELD_CHECK_MODAL', {
        title: '내용 입력',
        content: '내용을 입력하지 않은 곳은 사라집니다.\n모두 입력하셨습니까?',
        cancelText: '취소',
        id: editingDocumentId,
        inputs,
      })
    );
  } else {
    yield put(
      editingDocumentSubmitRequest({
        id: editingDocumentId as string,
        inputs,
      })
    );
  }
}

export default function* sagas() {
  yield all([
    takeEvery(EDITING_DOCUMENT__FIELD_DATA_SET, setFieldData),
    takeEvery(EDITING_DOCUMENT__FIELD_TARGET_DATA_SET, setFieldTargetData),
    takeEvery(EDITING_DOCUMENT__FILE_LOAD_REQUEST, loadFileSaga),
    takeLeading(EDITING_DOCUMENT__SUBMIT_REQUEST, submitEditingDocument),
    takeLeading(EDITING_DOCUMENT__COMPLETE_EDIT, completeEditEditingDocument),
  ]);
}
